import React, { useEffect, useMemo, useState } from "react";
import {
  format,
  startOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  isSameDay,
  parseISO,
} from "date-fns";
import { CalendarPlus, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const HOURS = Array.from({ length: 18 }, (_, i) => i + 6); // 06:00 – 23:00

// Defensive date parse — Auto-Pilot/automation events may have a missing or
// malformed date. Returns a valid Date or null (never an Invalid Date that
// would crash date-fns `format`).
const parseSafe = (s) => {
  if (!s) return null;
  const d = parseISO(s);
  return isNaN(d.getTime()) ? null : d;
};
const CHANNELS = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "blog", label: "Blog" },
  { id: "other", label: "Other" },
];

const CalendarPanel = () => {
  const [weekStart, setWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [events, setEvents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({
    title: "",
    date: format(new Date(), "yyyy-MM-dd"),
    time: "12:00",
    channel: "instagram",
    duration_minutes: 30,
    notes: "",
  });

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const load = async () => {
    try {
      const { data } = await api.get("/calendar/events");
      setEvents(data || []);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!draft.title.trim()) return toast.error("Add a title.");
    const isoLocal = `${draft.date}T${draft.time}:00`;
    const starts_at = new Date(isoLocal).toISOString();
    try {
      await api.post("/calendar/events", {
        title: draft.title,
        starts_at,
        duration_minutes: Number(draft.duration_minutes) || 30,
        channel: draft.channel,
        notes: draft.notes,
        kind: "post",
      });
      toast.success("Scheduled.");
      setShowCreate(false);
      setDraft({ ...draft, title: "", notes: "" });
      load();
    } catch (e) {
      toast.error("Couldn't schedule.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/calendar/events/${id}`);
      setEvents((cur) => cur.filter((e) => e.event_id !== id));
    } catch {}
  };

  const eventsByDay = (d) =>
    events.filter((e) => {
      const dt = parseSafe(e.starts_at);
      return dt && isSameDay(dt, d);
    });

  return (
    <div className="space-y-4" data-testid="calendar-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            data-testid="calendar-prev-week"
            onClick={() => setWeekStart(subWeeks(weekStart, 1))}
            className="p-2 border border-white/10 hover:border-white/30 rounded-sm"
          >
            <ChevronLeft size={16} className="text-slate-300" />
          </button>
          <p className="font-heading font-bold text-white text-lg">
            {format(weekStart, "MMM d")} – {format(addDays(weekStart, 6), "MMM d, yyyy")}
          </p>
          <button
            data-testid="calendar-next-week"
            onClick={() => setWeekStart(addWeeks(weekStart, 1))}
            className="p-2 border border-white/10 hover:border-white/30 rounded-sm"
          >
            <ChevronRight size={16} className="text-slate-300" />
          </button>
          <button
            data-testid="calendar-today"
            onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
            className="px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-slate-300 border border-white/10 hover:border-white/30 rounded-sm"
          >
            Today
          </button>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="calendar-new-event-btn"
          className="px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-[#33F3FF] flex items-center gap-2"
        >
          <CalendarPlus size={14} /> New Post
        </button>
      </div>

      {/* Week grid with hourly bands */}
      <div className="bg-[#0A192F] border border-white/10 rounded-sm overflow-x-auto">
        <div className="min-w-[920px] grid" style={{ gridTemplateColumns: "60px repeat(7, minmax(0,1fr))" }}>
          <div></div>
          {days.map((d) => (
            <div
              key={d.toISOString()}
              className="p-3 text-center border-b border-white/10 border-l border-white/5"
            >
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {format(d, "EEE")}
              </p>
              <p
                className={[
                  "font-heading text-lg font-bold",
                  isSameDay(d, new Date()) ? "text-cyan-300" : "text-white",
                ].join(" ")}
              >
                {format(d, "d")}
              </p>
            </div>
          ))}

          {HOURS.map((h) => (
            <React.Fragment key={h}>
              <div className="text-right pr-2 py-3 font-mono text-[10px] text-slate-500 border-r border-white/5">
                {String(h).padStart(2, "0")}:00
              </div>
              {days.map((d) => {
                const cellEvents = eventsByDay(d).filter(
                  (e) => parseSafe(e.starts_at)?.getHours() === h
                );
                return (
                  <div
                    key={`${d.toISOString()}-${h}`}
                    className="border-b border-l border-white/5 min-h-[44px] p-1"
                  >
                    {cellEvents.map((e) => (
                      <div
                        key={e.event_id}
                        data-testid={`calendar-event-${e.event_id}`}
                        className="group bg-cyan-400/10 border border-cyan-400/30 rounded-sm px-2 py-1 text-[11px] text-white truncate flex items-center justify-between"
                        title={`${e.title} @ ${format(parseSafe(e.starts_at), "HH:mm")}`}
                      >
                        <span className="truncate">
                          <span className="text-cyan-300 font-mono">
                            {format(parseSafe(e.starts_at), "HH:mm")}
                          </span>{" "}
                          {e.title}
                        </span>
                        <button
                          onClick={() => remove(e.event_id)}
                          className="opacity-0 group-hover:opacity-100 text-red-300"
                          data-testid={`calendar-delete-${e.event_id}`}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {showCreate && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4"
          onClick={() => setShowCreate(false)}
          data-testid="calendar-create-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#0A192F] border border-cyan-400/30 cf-glow rounded-sm p-6"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
              Schedule a post
            </p>
            <h4 className="font-heading text-xl font-bold text-white">New scheduled item</h4>

            <div className="mt-4 space-y-3">
              <input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Title"
                data-testid="calendar-input-title"
                className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={draft.date}
                  onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                  data-testid="calendar-input-date"
                  className="px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
                />
                <input
                  type="time"
                  value={draft.time}
                  onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                  data-testid="calendar-input-time"
                  className="px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={draft.channel}
                  onChange={(e) => setDraft({ ...draft, channel: e.target.value })}
                  data-testid="calendar-input-channel"
                  className="px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
                >
                  {CHANNELS.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="5"
                  value={draft.duration_minutes}
                  onChange={(e) => setDraft({ ...draft, duration_minutes: e.target.value })}
                  data-testid="calendar-input-duration"
                  placeholder="Duration (min)"
                  className="px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
                />
              </div>
              <textarea
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="Notes (optional)"
                rows={3}
                data-testid="calendar-input-notes"
                className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
              />
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setShowCreate(false)}
                data-testid="calendar-cancel"
                className="px-4 py-2 text-sm text-slate-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={create}
                data-testid="calendar-save"
                className="px-5 py-2 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-wider rounded-sm hover:bg-[#33F3FF]"
              >
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPanel;
